import { CopyIcon, FilePlus2Icon, ReceiptTextIcon, UsersIcon } from "lucide-react"
import type { LucideIcon } from "lucide-react"
import type * as React from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import type { Capability } from "@steelbill/types"
import { Card } from "@/components/ui/card"
import { useAuth } from "@/features/auth/auth-context"
import { cn } from "@/lib/utils"
import { routes } from "@/routes/paths"

/**
 * §11.2's four action tiles — "LARGEST ELEMENT, above the fold on a phone: New
 * Invoice · New Quotation · Repeat Last Bill · Customers".
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THESE COME BEFORE THE FIGURES. §1.1 rules out the admin-dashboard
 * template precisely because it "would put a row of statistics here and push the
 * buttons under the fold". The owner opens this app to MAKE something; the
 * making of it belongs under his thumb, and the figures belong below.
 *
 * THEY RENDER WITHOUT DATA. Every tile is a `<Link>`, so a workshop with one bar
 * still gets a working New Bill button while the summary request is in flight or
 * failing. The one exception is Repeat Last Bill, which needs to know WHICH bill
 * — and when there is no last bill it is not rendered at all rather than
 * rendered disabled. A disabled button on the first screen of a brand-new
 * install looks broken and cannot be explained; three tiles laid out
 * deliberately look like the app was built that way.
 *
 * ONE FILLED TILE, THE REST OUTLINED. Emphasis comes from being the only solid
 * shape rather than from a hue — which survives a colour-blind user and workshop
 * glare, neither of which is true of colour.
 * ─────────────────────────────────────────────────────────────────────────────
 */

interface Tile {
  id: string
  to: string
  labelKey: string
  helpKey: string
  icon: LucideIcon
  /** §2.1 — a capability, never a role string. A typo is a compile error. */
  capability: Capability
  primary?: boolean
}

export function ActionTiles({ lastBillId }: { lastBillId: number | null }): React.JSX.Element {
  const { t } = useTranslation()
  const { can } = useAuth()

  const tiles: Tile[] = [
    {
      id: "new-invoice",
      to: routes.invoiceNew,
      labelKey: "btn.newInvoice",
      helpKey: "home.help.newInvoice",
      icon: ReceiptTextIcon,
      capability: "invoice.create",
      primary: true,
    },
    {
      id: "new-quotation",
      to: routes.quotationNew,
      labelKey: "btn.newQuotation",
      helpKey: "home.help.newQuotation",
      icon: FilePlus2Icon,
      capability: "quotation.create",
    },
    /* §11.3 — "the highest-value shortcut in the whole app". It lands in the
     * invoice editor pre-filled from the last bill; the number, the dates and
     * the S.O. number are never carried over. */
    ...(lastBillId === null
      ? []
      : ([
          {
            id: "repeat-last-bill",
            to: routes.invoiceCopy(lastBillId),
            labelKey: "btn.repeatLastBill",
            helpKey: "home.help.repeatLastBill",
            icon: CopyIcon,
            capability: "invoice.create",
          },
        ] satisfies Tile[])),
    {
      id: "customers",
      to: routes.customers,
      labelKey: "btn.customers",
      helpKey: "home.help.customers",
      icon: UsersIcon,
      capability: "customer.view",
    },
  ]

  const visible = tiles.filter((tile) => can(tile.capability))

  return (
    <nav
      aria-label={t("home.actionsLabel")}
      className="grid grid-cols-2 gap-4 px-4 lg:px-6 @5xl/main:grid-cols-4"
    >
      {visible.map((tile) => (
        <Card
          key={tile.id}
          data-slot="card"
          className={cn(
            "@container/tile p-0",
            tile.primary
              ? "bg-primary text-primary-foreground"
              : "hover:bg-accent/50 transition-colors",
          )}
        >
          <Link
            to={tile.to}
            className="flex h-full min-h-28 flex-col items-start justify-between gap-3 p-4"
          >
            <span
              className={cn(
                "flex size-9 shrink-0 items-center justify-center rounded-md",
                tile.primary ? "bg-primary-foreground/15" : "bg-muted",
              )}
            >
              <tile.icon aria-hidden="true" className="size-5" />
            </span>
            <span className="flex min-w-0 flex-col gap-0.5">
              {/* Never an icon alone (§11.1), and never a fixed width — Marathi
                * runs longer than English (§11.4), so the label must wrap. */}
              <span className="text-balance font-semibold">{t(tile.labelKey)}</span>
              <span
                className={cn(
                  "text-sm",
                  tile.primary ? "text-primary-foreground/80" : "text-muted-foreground",
                )}
              >
                {t(tile.helpKey)}
              </span>
            </span>
          </Link>
        </Card>
      ))}
    </nav>
  )
}
